import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FileTextIcon, ReceiptIcon, RepeatIcon, RotateCcwIcon } from 'lucide-react';
import { toast } from 'sonner';
import { ProductImage } from '../../ui/ProductImage';
import { useCatalog } from '../../contexts/CatalogContext';
import { useCartActions, useOverlayActions } from '../../contexts/StoreContext';
import { useSignedInAccount } from '../../contexts/AccountContext';
import { salon } from '../../data/content';
import { euro } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { isSoldOut } from '../../core/rules/stock';
import { dayLong, dayShort, newestFirst, orderTotal, refillableFromOrders } from '../../utils/account';
import { giftCardCheck, isAwaitingDelivery, isGiftCardOnly, orderAnchor } from '../../utils/accountOrders';
import { useFeatures } from '../../hooks/useFeatures';
import { printInvoice } from '../../utils/invoice';
import { btnPrimary, btnQuiet } from '../../ui/styles';
import type { AccountOrder } from '../../core/types/account';
import { PageTitle } from './PageTitle';
import { EmptyCard } from './EmptyCard';
import { OrderTracking } from './OrderTracking';
import { orderStatusLabel as statusLabel, orderStatusTone } from './orderStatus';
import { DELIVERY_HREF } from './useAccountNav';

/** Een kapotte #-link (handmatig getypt) mag de pagina niet laten vallen. */
const safeDecode = (s: string) => {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
};

/**
 * Bestellingen & facturen — bewust sober. Voor een klant zonder abonnement is dit haar tweede tab:
 * eerst haar pakketten, en pas onderaan (zacht) het bijvullen.
 * `#bestelling-<id>` (vanaf het overzicht of de terugkeerpagina) scrollt naar die ene bestelling.
 */
export function Orders() {
  const {
    account
  } = useSignedInAccount();
  const {
    productById
  } = useCatalog();
  const {
    add
  } = useCartActions();
  const {
    openCart
  } = useOverlayActions();
  const features = useFeatures();
  const {
    hash
  } = useLocation();
  const orders = newestFirst(account.orders);
  const target = safeDecode(hash.slice(1));
  const isGift = giftCardCheck(productById);
  const sub = account.subscription;
  const offerRefill = features.subscriptions && !(sub && sub.status !== 'gestopt') && !orders.some(o => isAwaitingDelivery(o, isGift)) && refillableFromOrders(account, productById).length > 0;

  // Na het laden naar de gevraagde bestelling, met focus zodat een schermlezer daar begint.
  useEffect(() => {
    if (!target) return;
    const el = document.getElementById(target);
    if (!el) return;
    el.scrollIntoView({
      block: 'start'
    });
    el.focus({
      preventScroll: true
    });
  }, [target]);

  /** Alleen wat nog leverbaar is gaat in de mand; wat niet kan, zeggen we er eerlijk bij. */
  const reorder = (lines: AccountOrder['lines']) => {
    let added = 0;
    let skipped = 0;
    lines.forEach(l => {
      const p = productById(l.productId);
      if (!p || isSoldOut(p)) {
        skipped++;
        return;
      }
      for (let i = 0; i < l.qty; i++) add(p, {
        silent: true
      });
      added++;
    });
    if (!added) {
      toast.error('Deze producten zijn nu niet leverbaar');
      return;
    }
    if (skipped) toast(`${plural(skipped, 'product is', 'producten zijn')} nu niet leverbaar`, {
      description: 'De rest staat in je winkelmand.'
    });
    openCart();
  };
  const invoice = async (o: AccountOrder) => {
    const ok = await printInvoice(o, account.customer, salon, productById, dayShort(o.date));
    if (!ok) toast.error('De factuur lukte even niet', {
      description: 'Probeer het nog eens, of app Sarah: dan mailt ze hem.'
    });
  };
  return <div data-id="element-6334" data-mp="325b1naideian">
      <PageTitle lead={orders.length ? 'Facturen download je als PDF. Opnieuw bestellen is één tik.' : undefined} data-id="element-6335" data-mp="2x8klyoah9opr" />

      {orders.length === 0 ? <EmptyCard className="max-w-xl" icon={<ReceiptIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-6337" data-mp="cilppc2sp42z" />} title="Nog geen bestellingen." actions={<Link to="/webshop" className={btnPrimary} data-id="element-6338" data-mp="2i7nquy4hqjrc">
              Naar de webshop
            </Link>} data-id="element-6336" data-mp="hg3ucq9s4x56">
          {account.visits.length ? 'Behandelingen reken je in de salon af, die staan hier niet. Bestel je iets in de webshop, dan vind je het hier terug, met factuur.' : 'Bestel je iets in de webshop, dan vind je het hier terug, met factuur en een knop om het opnieuw te bestellen.'}
        </EmptyCard> : <ul className="space-y-4" data-id="element-6339" data-mp="364l0mkekgfbw">
          {orders.map(o => {
        const lines = o.lines.map(l => ({
          ...l,
          product: productById(l.productId)
        }));
        const total = orderTotal(o);
        const date = dayLong(o.date);
        const giftOnly = isGiftCardOnly(o, isGift);
        const label = statusLabel(o, giftOnly);
        return <li key={o.id} data-id="element-6340" data-mp="3jtv9vsnw8nar">
                <article id={orderAnchor(o.id)} tabIndex={-1} className={`scroll-mt-28 bg-shell outline-none ring-1 ${target === orderAnchor(o.id) ? 'ring-ink' : 'ring-line'}`} aria-labelledby={`order-${o.id}`} data-id="element-6341" data-mp="3out26y2maxpz">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4 sm:px-6" data-id="element-6342" data-mp="3p0jv3k9cg46o">
                    <div data-id="element-6343" data-mp="3l09tskwhy1y5">
                      <h2 id={`order-${o.id}`} className="text-[15px] font-normal text-ink" data-id="element-6344" data-mp="3l11civ4zw570">
                        <span className="capitalize" data-id="element-6345" data-mp="ljlbtcdq8jt9">{date}</span>
                      </h2>
                      <p className="text-[13px] text-muted" data-id="element-6346" data-mp="gmbe79g6p1uc">
                        <span className="sr-only" data-id="element-6347" data-mp="btrxi3m79qlb">Bestelnummer </span>#{o.id}
                        {o.viaSubscription && <span className="ml-2 inline-flex items-center gap-1" data-id="element-6348" data-mp="7rwyj5rqrpap">
                            <RepeatIcon className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" data-id="element-6349" data-mp="14aujdg3xsyi7" /> Abonnement
                          </span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-6350" data-mp="2tt17pb9v8m11">
                      <span className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] ${orderStatusTone(label)}`} data-id="element-6351" data-mp="2yqjht4zmd7am">
                        <span className="sr-only" data-id="element-6352" data-mp="2k0vha5z9icny">Status: </span>
                        {label}
                      </span>
                      <span className="text-[15px] tabular-nums text-ink" data-id="element-6353" data-mp="2ozf0vzqdvcd5">
                        <span className="sr-only" data-id="element-6354" data-mp="2b070j3ihhy7d">Totaal </span>
                        {euro(total, {
                    cents: true
                  })}
                      </span>
                    </div>
                  </div>
                  <ul className="flex flex-wrap gap-2 px-5 py-4 sm:px-6" aria-label="Producten" data-id="element-6355" data-mp="21441i6ixifon">
                    {lines.map(l => <li key={l.productId} className="flex min-w-0 items-center gap-2.5 bg-sand py-1.5 pl-1.5 pr-3" data-id="element-6356" data-mp="26487v3q4hu15">
                        <span className="h-9 w-9 shrink-0 bg-shell" data-id="element-6357" data-mp="4asfa1ah653j">{l.product && <ProductImage src={l.product.image} alt="" className="h-full w-full p-0.5" data-id="element-6358" data-mp="4a8wyjgwxeoz" />}</span>
                        <span className="max-w-[16rem] truncate text-[13px] text-ink" data-id="element-6359" data-mp="1ot2yftvyzv58">
                          {l.qty > 1 ? `${l.qty}× ` : ''}
                          {l.product?.name ?? l.productId}
                        </span>
                      </li>)}
                  </ul>
                  {o.cancelledAt ? <p className="border-t border-line bg-sand/50 px-5 py-3.5 text-[14px] text-muted sm:px-6" data-id="element-6360" data-mp="27j17ac7lds9x">
                      Geannuleerd op {dayLong(o.cancelledAt)}. Had je al betaald, dan staat het geld binnen een paar werkdagen terug op je rekening.
                    </p> : giftOnly ? <p className="border-t border-line px-5 py-3.5 text-[14px] text-muted sm:px-6" data-id="element-6361" data-mp="2ci4d3yvec2iy">
                      Er gaat niets op de post: de cadeaubon met zijn code staat in je bevestigingsmail.
                    </p> : <OrderTracking order={o} postcode={account.customer.address.postcode} data-id="element-6362" data-mp="i6h4n9jl03k3" />}
                  <div className="flex flex-wrap gap-1 border-t border-line px-3 py-2 sm:px-4" data-id="element-6363" data-mp="1aac1ax846fo">
                    <button type="button" onClick={() => reorder(o.lines)} aria-label={`Bestelling van ${date} opnieuw bestellen`} className={btnQuiet} data-id="element-6364" data-mp="17xo8h4sb1up">
                      <RotateCcwIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6365" data-mp="ays9bi6anvek" />
                      Bestel opnieuw
                    </button>
                    <button type="button" onClick={() => invoice(o)} aria-label={`Factuur van ${date} openen als PDF`} className={btnQuiet} data-id="element-6366" data-mp="3egafll6f4ucv">
                      <FileTextIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6367" data-mp="3jec0mbnen3qz" />
                      Factuur (PDF)
                    </button>
                  </div>
                </article>
              </li>;
      })}
        </ul>}

      {/* Zacht, onderaan, en pas als er niets meer onderweg is. */}
      {offerRefill && <p className="mt-8 flex items-start gap-3 border-t border-line pt-6 text-[14px] leading-relaxed text-muted" data-id="element-6368" data-mp="2o5rwbhk8c4vp">
          <RepeatIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6369" data-mp="2j88iujvcvw4t" />
          <span data-id="element-6370" data-mp="2ef5ls1rde4jv">
            Liever niet zelf onthouden wanneer iets op raakt? Met Bewust Abonnement komen je producten vanzelf, zonder verzendkosten.{' '}
            <Link to={DELIVERY_HREF} className="whitespace-nowrap text-ink underline underline-offset-4 hover:text-clay" data-id="element-6371" data-mp="24jc2lzj5bct4">
              Zo werkt het
            </Link>
          </span>
        </p>}
    </div>;
}